import { CIVS } from '../gameplay/civilisations';
import type { CommandEnvelope } from '../engine/simulation/commands';
import { MIN_SIM_VERSION, SIM_VERSION, Simulation, type GameConfig } from '../engine/simulation/simulation';
import { putRecord } from '../persistence/db';

export const REPLAY_VERSION = 1;

/**
 * A replay stores only the initial configuration and the ordered human
 * commands. AI players are deterministic and regenerate their own commands.
 * Periodic state hashes allow playback to be validated.
 */
export interface Replay {
  replayVersion: number;
  simVersion: number;
  config: GameConfig;
  commands: CommandEnvelope[];
  finalTick: number;
  checkpoints: [number, number][];
}

export function recordReplay(sim: Simulation): Replay {
  const humans = new Set(sim.config.players.map((p, i) => (p.ai ? -1 : i)).filter((i) => i >= 0));
  const checkpoints = [...sim.hashes].filter(([t]) => t % 200 === 0);
  return {
    replayVersion: REPLAY_VERSION,
    simVersion: SIM_VERSION,
    config: sim.config,
    commands: sim.commandLog.filter((c) => humans.has(c.player)),
    finalTick: sim.tick,
    checkpoints,
  };
}

export function validateReplay(r: Replay): string | null {
  if (!r || r.replayVersion !== REPLAY_VERSION) return 'Unsupported replay format.';
  if (!(r.simVersion >= MIN_SIM_VERSION && r.simVersion <= SIM_VERSION)) return `Replay requires simulation rules v${r.simVersion} (this build plays v${MIN_SIM_VERSION} to v${SIM_VERSION}).`;
  return null;
}

/** The game configuration to play a replay back with: under the rules it was recorded with. */
export function replayConfig(r: Replay): GameConfig {
  return { ...r.config, simVersion: r.simVersion };
}

/** Re-simulate a replay headlessly and verify checksums. Returns the first mismatching tick or -1. */
export function verifyReplay(r: Replay, maxTicks = Infinity): number {
  const sim = new Simulation(replayConfig(r));
  const byTick = new Map<number, CommandEnvelope[]>();
  for (const c of r.commands) (byTick.get(c.tick) ?? byTick.set(c.tick, []).get(c.tick)!).push(c);
  const checks = new Map(r.checkpoints);
  const end = Math.min(r.finalTick, maxTicks);
  while (sim.tick < end) {
    for (const c of byTick.get(sim.tick + 1) ?? []) sim.schedule(c);
    sim.step();
    const h = checks.get(sim.tick);
    if (h !== undefined && h !== sim.hash()) return sim.tick;
    if (sim.world.gameOver) break;
  }
  return -1;
}

export async function autosaveReplay(sim: Simulation): Promise<void> {
  try {
    const r = recordReplay(sim);
    const p = sim.world.players.map((pl) => CIVS[pl.civ].name).join(' vs ');
    const d = new Date();
    await putRecord('replays', {
      name: `Replay ${d.toISOString().slice(0, 19).replace('T', ' ')}`,
      date: Date.now(),
      meta: { players: p, map: sim.config.mapType, minutes: Math.floor(sim.tick / 1200) },
      data: JSON.stringify(r),
    });
  } catch (e) {
    console.warn('Replay autosave failed', e);
  }
}
